import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import workCategories from '../data/portfolio/workCategories';

export default function WorkGallery() {
  const { categorySlug } = useParams();
  const category = workCategories.find(work => work.slug === categorySlug);
  const isSample = category?.isSample !== false;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [categorySlug]);

  if (!category) {
    return <main className="rg-page rg-work-page rg-container">
      <Helmet><title>Collection not found | Resham Gurung</title></Helmet>
      <h1>Collection not found</h1>
      <Link className="rg-work-back" to="/#work">Back to my work</Link>
    </main>;
  }

  const editorial = category.layout === 'editorial';
  let offset = 0;
  const rows = (category.galleryRows || []).map(count => {
    const start = offset;
    offset += count;
    return { start, images: category.images.slice(start, offset) };
  });
  const renderPhoto = (image, index, rowCount) => {
    const ratio = image.width && image.height ? image.width / image.height : 1;
    const maxWidth = Math.min(760, Math.round((rowCount === 1 ? 440 : rowCount === 2 ? 360 : 320) * ratio));
    const sizes = editorial
      ? `(max-width: 700px) ${rowCount === 1 ? '85vw' : '45vw'}, (max-width: 1100px) 45vw, ${maxWidth}px`
      : '(max-width: 768px) calc(100vw - 48px), (max-width: 1228px) calc((100vw - 74px) / 2), 577px';
    return <figure key={image.src} style={editorial ? { '--photo-ratio': ratio } : undefined}>
      <img
        src={image.src}
        srcSet={image.srcSet}
        sizes={sizes}
        width={image.width}
        height={image.height}
        alt={image.alt}
        loading={index === 0 ? 'eager' : 'lazy'}
        decoding="async"
      />
    </figure>;
  };

  return <main className="rg-page rg-work-page">
    <Helmet>
      <title>{category.title} Portfolio | Resham Gurung</title>
      <meta name="description" content={`${category.title} photography and retouching collection. ${category.description}`} />
    </Helmet>
    <div className="rg-container">
      <Link className="rg-work-back" to="/#work">← Back to my work</Link>
      <header className="rg-work-heading">
        <p className="rg-work-eyebrow">THE COLLECTION</p>
        <h1>{category.title}<em>.</em></h1>
        <p>{category.description}</p>
        {isSample && <span className="rg-work-sample-note">Sample collage · Placeholder photography</span>}
      </header>
      <nav className="rg-work-collections" aria-label="Explore work collections">
        <p className="rg-work-eyebrow">EXPLORE THE COLLECTIONS</p>
        <div>{workCategories.map(work => <Link
          key={work.slug}
          to={`/work/${work.slug}`}
          aria-current={work.slug === categorySlug ? 'page' : undefined}
        >{work.title}</Link>)}</div>
      </nav>
      <section
        className={`rg-work-collage rg-work-collage--${category.slug}${category.layout ? ` rg-work-collage--${category.layout}` : ''}${category.galleryStyle ? ` rg-work-collage--${category.galleryStyle}` : ''}`}
        aria-label={`${category.title} ${isSample ? 'sample photo collage' : 'photo gallery'}`}
      >
        {editorial ? rows.map((row, rowIndex) => <div
          key={`${category.slug}-${rowIndex}`}
          className={`rg-work-row rg-work-row--${row.images.length === 1 ? 'solo' : row.images.length === 2 ? 'duo' : 'trio'}`}
        >{row.images.map((image, index) => renderPhoto(image, row.start + index, row.images.length))}</div>)
          : category.images.map((image, index) => renderPhoto(image, index))}
      </section>
      <section className="rg-work-inquiry">
        <h2>Have something in mind?</h2>
        <p>Let’s give your images the care they deserve.</p>
        <Link to="/contacts?request=quote">Tell me about your project <span aria-hidden="true">↗</span></Link>
      </section>
    </div>
  </main>;
}
